import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { useMediaQuery } from 'react-responsive';

import * as appActions from "../../core/app.store";
import * as config from '../../common/config';
import * as cuisineOrderManagementAction from "../../redux/store-sarang/cuisine-order/cuisine-order-management.store";
import * as accountManagementAction from "../../redux/store/account/account.store";

import { getUserInfo, getCookies } from "../../utils/configuration";
import ModalSubmitForm from '../../components/sarang-modals/modal-submit-form/modal-submit-form';
import ModalConfirm from '../../components/sarang-modals/modal-confirm/modal-confirm';
import FormAddEditCuisineOrder from './components/form-add-edit-cuisine-order-multistep';

// Hooks
import { useLookupDistrict } from '../../hooks/useLookupDistrict';
import { useLookupCommune } from '../../hooks/useLookupCommune';
import { useLookupHerdsman } from '../../hooks/useLookupHerdsman';
import { useLookupBranch } from '../../hooks/useLookupBranch';
import { useLookupDepartment } from '../../hooks/useLookupDepartment';
import { useCuisineOrderList } from './hooks/useCuisineOrderList';
import { useCuisineOrderActions } from './hooks/useCuisineOrderActions';
import { useCuisineOrderNotifications } from './hooks/useCuisineOrderNotifications';

// Components
import CuisineOrderSearch from './components/CuisineOrderSearch';
import CuisineOrderTable from './components/CuisineOrderTable';
import CuisineOrderMobileList from './components/CuisineOrderMobileList';

const configLocal = {
    defaultPageSize: config.Configs.DefaultPageSize,
    sortExpression: "orderDate desc" + (new Date().getHours() <= 13 ? ', receiptTime asc' : ', receiptTime desc'),
    orderBy: "orderDate",
    order: "desc",
};

export default function CuisineOrderList() {
    const isDesktopOrLaptop = useMediaQuery({ query: "(min-width: 1024px)" });
    const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1023px)" });
    // Hôm nay theo lịch Việt Nam (nửa đêm local) để mặc định "Từ ngày" không bị lệch múi giờ máy
    const now = dayjs(dayjs().tz().format('YYYY-MM-DD'));
    const dispatch = useDispatch();
    const showLoading = (data) => dispatch(appActions.ShowLoading(data));
    const deviceId = getCookies('DeviceId');

    const { register, handleSubmit, setValue, control, getValues } = useForm({
        mode: "all",
        reValidateMode: "onBlur",
    });

    const [user] = useState(getUserInfo());
    const [openSearch, setOpenSearch] = useState(false);
    const [setting, setSetting] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [receiptTimeOrder] = useState(new Date().getHours() <= 13 ? ', receiptTime asc' : ', receiptTime desc');

    // Hooks
    const { district, getLookupDistrict } = useLookupDistrict();
    const { commune, getLookupCommune } = useLookupCommune();
    const { herdsman, herdsmanAll, herdsmanGv, getLookupHerdsman, getLookupHerdsmanGV, getLookupHerdsmanAll } = useLookupHerdsman();
    const { branch, getLookupBranch } = useLookupBranch();
    const { departmentAll, getListNoPermiss } = useLookupDepartment();

    const {
        data,
        totalItemCount,
        page, setPage,
        rowsPerPage, setRowsPerPage,
        order, setOrder,
        orderBy, setOrderBy,
        searchData, setSearchData,
        sumaryCuisine,
        getListCuisineOrderManagement
    } = useCuisineOrderList(showLoading, configLocal);

    const {
        buttonOpenAddEditRef, buttonOpenConfirmRef,
        isOpenAddEditDialog,
        isOpenDeleteDialog, setOpenDeleteDialog,
        isOpenActiveDialog,
        isOpenDeactiveDialog,
        isOpenChangeStatusDialog, setOpenChangeStatusDialog,
        isOpenChangeConfirmDialog, setOpenChangeConfirmDialog,
        isOpenChangeDeliveryDialog,
        selectedItem,
        openAddDialog, openEditDialog, closeAddEditDialog,
        openConfirmDialog, closeConfirmDialog,
        handleSubmitAddEditCuisineOrder, handleConfirm
    } = useCuisineOrderActions(
        showLoading, getListCuisineOrderManagement, user,
        page, rowsPerPage, orderBy, order, receiptTimeOrder, searchData
    );

    useCuisineOrderNotifications(
        getListCuisineOrderManagement, page, rowsPerPage, orderBy, order, receiptTimeOrder, searchData, deviceId
    );

    const fetchData = useCallback(async () => {
        showLoading(true);
        try {
            await Promise.allSettled([
                getLookupDistrict(),
                getLookupHerdsman(),
                getLookupHerdsmanAll(),
                getLookupHerdsmanGV(),
                getListNoPermiss(),
                getLookupBranch(),
                GetCuisineOrderSettings(),
            ]);
        } finally {
            showLoading(false);
        }
    }, [getLookupDistrict, getLookupHerdsman, getLookupHerdsmanAll, getLookupHerdsmanGV, getListNoPermiss, getLookupBranch, showLoading]);

    const GetCuisineOrderSettings = async () => {
        const res = await cuisineOrderManagementAction.GetCuisineOrderSettings();
        if (res && res.content) setSetting(res.content);
    };

    const getUserDetail = useCallback(async () => {
        try {
            const res = await accountManagementAction.GetDetailAcc();
            if (res && res.content && res.content.branchId) {
                const branchId = res.content.branchId;
                setValue("branchId", branchId);
                setSearchData(prev => ({ ...prev, branchId }));
                getListCuisineOrderManagement(1, rowsPerPage, configLocal.sortExpression, { ...getValues(), branchId });
                return;
            }
            getListCuisineOrderManagement(1, rowsPerPage, configLocal.sortExpression, getValues());
        } catch (err) {
            getListCuisineOrderManagement(1, rowsPerPage, configLocal.sortExpression, getValues());
        }
    }, [rowsPerPage, setValue, getValues, getListCuisineOrderManagement, setSearchData]);

    useEffect(() => {
        const initialDate = dayjs(now).format("YYYY-MM-DD");
        setValue("orderFromDate", initialDate);
        setSearchData({ orderFromDate: initialDate });
        getUserDetail();
        fetchData();
    }, []);

    useEffect(() => {
        if (selectedDistrict) getLookupCommune(selectedDistrict.value);
    }, [selectedDistrict, getLookupCommune]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);
        const sortExpression = property + ' ' + newOrder + ((property && property !== "receiptTime") ? receiptTimeOrder : '');
        getListCuisineOrderManagement(page + 1, rowsPerPage, sortExpression, searchData);
    };

    const handleChangePage = (newPage) => {
        setPage(newPage - 1);
        const sortExpression = orderBy + ' ' + order + ((orderBy && orderBy !== "receiptTime") ? receiptTimeOrder : '');
        getListCuisineOrderManagement(newPage, rowsPerPage, sortExpression, searchData);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        const sortExpression = orderBy + ' ' + order + ((orderBy && orderBy !== "receiptTime") ? receiptTimeOrder : '');
        getListCuisineOrderManagement(1, newRowsPerPage, sortExpression, searchData);
    };

    const onSubmit = (data) => {
        setSearchData(data);
        const sortExpression = orderBy + ' ' + order + ((orderBy && orderBy !== "receiptTime") ? receiptTimeOrder : '');
        setPage(0);
        getListCuisineOrderManagement(1, rowsPerPage, sortExpression, data);
        if (isTabletOrMobile) setOpenSearch(false);
    };

    const renderFormSearch = () => (
        <CuisineOrderSearch
            register={register} handleSubmit={handleSubmit} onSubmit={onSubmit}
            setValue={setValue} control={control} branch={branch}
            departmentAll={departmentAll} herdsmanAll={herdsmanAll} district={district}
            setSelectedDistrict={setSelectedDistrict} openAddDialog={openAddDialog} now={now}
        />
    );

    return (
        <div className="row">
            <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
                {isDesktopOrLaptop && (
                    <CuisineOrderTable
                        isTabletOrMobile={isTabletOrMobile} openSearch={openSearch} setOpenSearch={setOpenSearch}
                        openAddDialog={openAddDialog} renderFormSearch={renderFormSearch} sumaryCuisine={sumaryCuisine}
                        rowsPerPage={rowsPerPage} handleChangeRowsPerPage={handleChangeRowsPerPage}
                        handleRequestSort={handleRequestSort} order={order} orderBy={orderBy}
                        totalItemCount={totalItemCount} setRowsPerPage={setRowsPerPage} page={page}
                        handleChangePage={handleChangePage} configLocal={configLocal}
                        getListCuisineOrderManagement={getListCuisineOrderManagement} searchData={searchData}
                        data={data} receiptTimeOrder={receiptTimeOrder} setOpenChangeStatusDialog={setOpenChangeStatusDialog}
                        openConfirmDialog={openConfirmDialog} setOpenChangeConfirmDialog={setOpenChangeConfirmDialog}
                        setOpenDeleteDialog={setOpenDeleteDialog} openEditDialog={openEditDialog}
                    />
                )}
                {isTabletOrMobile && (
                    <CuisineOrderMobileList
                        handleSortChange={(sort) => getListCuisineOrderManagement(page + 1, rowsPerPage, sort, searchData)}
                        setOrder={setOrder} setOrderBy={setOrderBy} configLocal={configLocal}
                        getListCuisineOrderManagement={getListCuisineOrderManagement} searchData={searchData}
                        sumaryCuisine={sumaryCuisine} data={data} totalItemCount={totalItemCount}
                        rowsPerPage={rowsPerPage} page={page} handleChangePage={handleChangePage}
                        isTabletOrMobile={isTabletOrMobile} openSearch={openSearch} setOpenSearch={setOpenSearch}
                        openAddDialog={openAddDialog} renderFormSearch={renderFormSearch}
                        setOpenChangeStatusDialog={setOpenChangeStatusDialog} openConfirmDialog={openConfirmDialog}
                        setOpenChangeConfirmDialog={setOpenChangeConfirmDialog} setOpenDeleteDialog={setOpenDeleteDialog}
                        openEditDialog={openEditDialog}
                    />
                )}
            </div>

            <button ref={buttonOpenAddEditRef} type="button" className="d-none" data-toggle="modal" data-target="#modalSubmitForm" />
            <button ref={buttonOpenConfirmRef} type="button" className="d-none" data-toggle="modal" data-target="#modalConfirm" />

            <ModalSubmitForm
                title={!selectedItem ? "Đăng ký mới suất ăn" : "Sửa đơn đăng ký suất ăn"}
                open={isOpenAddEditDialog} onClose={closeAddEditDialog} hidefooter={true}
            >
                <FormAddEditCuisineOrder
                    district={district} herdsman={herdsman}
                    updateItem={selectedItem} onSubmitAddEdit={handleSubmitAddEditCuisineOrder} setting={setting}
                />
            </ModalSubmitForm>

            <ModalConfirm
                title="Xác nhận"
                prompt={
                    isOpenDeleteDialog ? "Bạn có chắc chắn muốn xóa không?" :
                        isOpenActiveDialog ? "Bạn có chắc chắn muốn xác nhận không?" :
                            isOpenDeactiveDialog ? "Bạn có chắc chắn muốn hủy xác nhận không?" :
                                isOpenChangeStatusDialog ? `Bạn có chắc chắn muốn thay đổi trạng thái thành <b>"${selectedItem?.orderStatusNameChange}"</b>?` :
                                    isOpenChangeConfirmDialog ? `Bạn có chắc chắn muốn bản ghi đăng ký suất này ăn được xác nhận bởi a/c/e <b>"${selectedItem?.herdsManConfirmName}"</b>?` :
                                        isOpenChangeDeliveryDialog ? `Bạn có chắc chắn muốn bản ghi đăng ký suất này được giao vận bởi a/c/e <b>"${selectedItem?.herdsManDeliveryName}"</b>?` : ""
                }
                open={isOpenDeleteDialog || isOpenActiveDialog || isOpenDeactiveDialog || isOpenChangeStatusDialog || isOpenChangeConfirmDialog || isOpenChangeDeliveryDialog}
                onClose={closeConfirmDialog} onConfirm={handleConfirm}
            />
        </div>
    );
}